import React, { useState, useEffect } from 'react';
import appInsightsService from '../services/appInsights';

const AppInsightsTest = () => {
  const [logMessage, setLogMessage] = useState('');
  const [testResults, setTestResults] = useState([]);
  
  // Function to add a test result
  const addResult = (test, success, details) => {
    setTestResults(prev => [...prev, { test, success, details, timestamp: new Date().toISOString() }]);
  };
  
  // Function to test logging
  const testLogging = () => {
    try {
      const message = logMessage || 'Test log message';
      
      // Clear previous results
      setTestResults([]);
      
      // Test event tracking
      try {
        appInsightsService.trackEvent('TestEvent', { source: 'AppInsightsTest' });
        addResult('Track Event', true, 'Event sent to Application Insights');
      } catch (error) {
        addResult('Track Event', false, `Error: ${error.message}`);
      }
      
      // Test trace tracking
      try {
        appInsightsService.trackTrace(message, { source: 'AppInsightsTest' });
        addResult('Track Trace', true, 'Trace sent to Application Insights');
      } catch (error) {
        addResult('Track Trace', false, `Error: ${error.message}`);
      }
      
      // Test metric tracking
      try {
        appInsightsService.trackMetric({
          name: 'TestMetric',
          average: 42,
          sampleCount: 1
        });
        addResult('Track Metric', true, 'Metric sent to Application Insights');
      } catch (error) {
        addResult('Track Metric', false, `Error: ${error.message}`);
      }
      
      // Test exception tracking
      try {
        appInsightsService.trackException(new Error('Test exception'), { source: 'AppInsightsTest' });
        addResult('Track Exception', true, 'Exception sent to Application Insights');
      } catch (error) {
        addResult('Track Exception', false, `Error: ${error.message}`);
      }
      
      // Test page view tracking
      try {
        appInsightsService.trackPageView('TestPage', window.location.pathname, { source: 'AppInsightsTest' });
        addResult('Track Page View', true, 'Page view sent to Application Insights');
      } catch (error) {
        addResult('Track Page View', false, `Error: ${error.message}`);
      }
      
      // Test web vitals
      try {
        const mockWebVital = {
          id: 'test-cls',
          name: 'CLS',
          value: 0.1,
          label: 'web-vital'
        };
        
        // Run through the same handler used in index.js
        const webVitalsHandler = (metric) => {
          try {
            const name = metric.name || metric.id;
            const value = metric.value || metric.delta;
            const label = metric.label || 'unknown';
            
            appInsightsService.trackMetric({
              name: `Web Vital: ${name} (${label})`,
              average: value,
              sampleCount: 1
            });
            return true;
          } catch (err) {
            console.warn('Failed to track web vital metric:', err);
            return false;
          }
        };
        
        const result = webVitalsHandler(mockWebVital);
        addResult('Web Vitals Handler', result, result ? 'Web vital metric processed successfully' : 'Failed to process web vital');
      } catch (error) {
        addResult('Web Vitals Handler', false, `Error: ${error.message}`);
      }
      
    } catch (error) {
      addResult('Overall Test', false, `Unexpected error: ${error.message}`);
    }
  };
  
  // Test if App Insights is initialized
  useEffect(() => {
    const isInitialized = appInsightsService.initialized;
    addResult(
      'App Insights Initialized', 
      isInitialized, 
      isInitialized ? 'Application Insights is properly initialized' : 'Application Insights is NOT initialized'
    );
  }, []);
  
  return (
    <div style={{ padding: '20px' }}>
      <h1>Application Insights Test</h1>
      
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          value={logMessage}
          onChange={(e) => setLogMessage(e.target.value)}
          placeholder="Message to log"
          style={{ padding: '8px', marginRight: '10px', width: '300px' }}
        />
        <button 
          onClick={testLogging}
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#0078d4', 
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test App Insights Logging
        </button>
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <h2>Test Results</h2>
        {testResults.length === 0 ? (
          <p>No tests run yet. Click the button above to test Application Insights.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Test</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Result</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Details</th>
                <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {testResults.map((result, index) => (
                <tr key={index}>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.test}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                    <span style={{ 
                      color: result.success ? 'green' : 'red',
                      fontWeight: 'bold'
                    }}>
                      {result.success ? '✓ Success' : '✗ Failed'}
                    </span>
                  </td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.details}</td>
                  <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{result.timestamp}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      
      <div style={{ marginTop: '20px' }}>
        <h2>Troubleshooting Tips</h2>
        <ul>
          <li>Check browser console for any errors or warnings</li>
          <li>Verify that your Application Insights instrumentation key is correct</li>
          <li>It may take a few minutes for logs to appear in the Azure portal</li>
          <li>Make sure the Application Insights resource is in the same region as your app</li>
        </ul>
      </div>
    </div>
  );
};

export default AppInsightsTest;
